import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Select,
  MenuItem,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import MetricCard from "../../../app_components/MetricCard";
import AgGrid from "../../../components/grid/AgGrid";
import {
  leftAlignColDef,
  rightAlignColDef,
  centerAlignColDef,
  defaultColDef,
} from "../../../components/grid/columnDefinitions/columnDefinitions";
import { ORG_PALETTE, getOrgTokens } from "../../../theme/style/organizationPalette";
import config from "../../../config";
import { fetchWithToken } from "../../../authentication/AuthProvider";
import "../../../style/pages-style/Engagement/engagement.css";
import ColumnVisibilityButton from "../../../components/button/ColumnVisibilityButton";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import SegmentedTabs from "../../../components/tabs/SegmentedTabs";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import arivufoot from "../../../assets/logo/foot.png";

const UNIFORMAT_TAXONOMY = {
  "A": {
    desc: "SUBSTRUCTURE",
    level2: {
      "A10": {
        desc: "Foundations",
        level3: {
          "A1010": "Standard Foundations",
          "A1020": "Special Foundations",
          "A1030": "Slab on Grade"
        }
      },
      "A20": {
        desc: "Basement Construction",
        level3: {
          "A2010": "Basement Walls",
          "A2020": "Basement Slabs"
        }
      }
    }
  },
  "B": {
    desc: "SHELL",
    level2: {
      "B10": {
        desc: "Superstructure",
        level3: {
          "B1010": "Floor Construction",
          "B1020": "Roof Construction"
        }
      },
      "B20": {
        desc: "Exterior Enclosure",
        level3: {
          "B2010": "Exterior Walls",
          "B2020": "Exterior Windows",
          "B2030": "Exterior Doors"
        }
      },
      "B30": {
        desc: "Roofing",
        level3: {
          "B3010": "Roof Coverings",
          "B3020": "Roof Openings"
        }
      }
    }
  },
  "C": {
    desc: "INTERIORS",
    level2: {
      "C10": {
        desc: "Interior Construction",
        level3: {
          "C1010": "Interior Partitions",
          "C1020": "Interior Doors",
          "C1030": "Fittings"
        }
      },
      "C20": {
        desc: "Stairs",
        level3: {
          "C2010": "Stair Construction",
          "C2020": "Stair Finishes & Railings"
        }
      },
      "C30": {
        desc: "Interior Finishes",
        level3: {
          "C3010": "Wall Finishes",
          "C3020": "Floor Finishes",
          "C3030": "Ceiling Finishes"
        }
      }
    }
  },
  "D": {
    desc: "SERVICES",
    level2: {
      "D10": { desc: "Conveying", level3: { "D1010": "Elevators & Escalators" } },
      "D20": { desc: "Plumbing", level3: { "D2010": "Plumbing Fixtures", "D2020": "Domestic Water Piping" } },
      "D30": { desc: "HVAC", level3: { "D3010": "HVAC Distribution", "D3020": "Air Handling Units" } },
      "D40": { desc: "Fire Protection", level3: { "D4010": "Fire Suppression Systems" } },
      "D50": { desc: "Electrical", level3: { "D5010": "Electrical Service & Distribution", "D5020": "Lighting & Branch Wiring" } }
    }
  },
  "E": {
    desc: "EQUIPMENT & FURNISHINGS",
    level2: {
      "E10": { desc: "Equipment", level3: { "E1010": "Commercial Equipment" } },
      "E20": { desc: "Furnishings", level3: { "E2010": "Fixed & Movable Furnishings" } }
    }
  },
  "F": {
    desc: "SPECIAL CONSTRUCTION & DEMOLITION",
    level2: {
      "F10": { desc: "Special Construction", level3: { "F1010": "Special Structures" } },
      "F20": { desc: "Selective Building Demolition", level3: { "F2010": "Building Elements Demolition" } }
    }
  },
  "G": {
    desc: "BUILDING SITEWORK",
    level2: {
      "G10": { desc: "Site Preparations", level3: { "G1010": "Site Clearing" } },
      "G20": { desc: "Site Improvements", level3: { "G2010": "Roadways & Paving" } },
      "G30": { desc: "Site Civil Mechanical Utilities", level3: { "G3010": "Water Supply & Sewer" } },
      "G40": { desc: "Site Electrical Utilities", level3: { "G4010": "Site Lighting" } }
    }
  },
  "U": {
    desc: "UNMAPPED",
    level2: {
      "U00": { desc: "Unmapped Elements", level3: { "U0000": "Unmapped Element" } }
    }
  }
};

const SECTION_TO_UNIFORMAT_FILTER = {
  substructure: {
    level1: "A",
    level2: {
      foundations: "A10",
      basement_construction: "A20",
    },
  },
  shell: {
    level1: "B",
    level2: {
      superstructure: "B10",
      exterior_enclosure: "B20",
      roofing: "B30",
    },
  },
  interiors: {
    level1: "C",
    level2: {
      interior_construction: "C10",
      stairs: "C20",
      interior_finishes: "C30",
    },
  },
  services: {
    level1: "D",
    level2: {
      conveying: "D10",
      plumbing: "D20",
      hvac: "D30",
      fire_protection: "D40",
      electrical: "D50",
    },
  },
  equipment_furnishings: {
    level1: "E",
    level2: {
      equipment: "E10",
      furnishings: "E20",
    },
  },
};

const getUniformatFilterFromSection = (selectedBuildingSection) => {
  if (!selectedBuildingSection?.categoryId) {
    return null;
  }

  if (selectedBuildingSection.categoryId === "whole_building") {
    return { noRows: true };
  }

  const sectionMap = SECTION_TO_UNIFORMAT_FILTER[selectedBuildingSection.categoryId];
  if (!sectionMap) {
    return null;
  }

  if (!selectedBuildingSection.subcategoryId) {
    return {
      level1: sectionMap.level1,
    };
  }

  const level2 = sectionMap.level2[selectedBuildingSection.subcategoryId];
  return {
    level1: sectionMap.level1,
    level2,
  };
};

const UniformatSelectRenderer = ({ rowData, level, onLocalChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const l1Code = rowData.uniformat_level_1_code || "U";
  const l2Code = rowData.uniformat_level_2_code || "U00";
  const l3Code = rowData.uniformat_level_3_code || "U0000";

  const handleLevelChange = (event) => {
    const selectedValue = event.target.value;
    let targetL3Code = l3Code;

    if (level === 1) {
      const l2s = UNIFORMAT_TAXONOMY[selectedValue]?.level2 || {};
      const firstL2Key = Object.keys(l2s)[0] || "U00";
      const l3s = l2s[firstL2Key]?.level3 || {};
      targetL3Code = Object.keys(l3s)[0] || "U0000";
    } else if (level === 2) {
      const l3s = UNIFORMAT_TAXONOMY[l1Code]?.level2[selectedValue]?.level3 || {};
      targetL3Code = Object.keys(l3s)[0] || "U0000";
    } else if (level === 3) {
      targetL3Code = selectedValue;
    }

    onLocalChange(rowData.id, targetL3Code);
  };

  if (level === 1) {
    const options = Object.keys(UNIFORMAT_TAXONOMY).map(code => ({
      code,
      desc: UNIFORMAT_TAXONOMY[code].desc
    }));
    return (
      <Select
        value={l1Code}
        onChange={handleLevelChange}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        size="small"
        variant="standard"
        disableUnderline
        sx={{
          fontFamily: "Poppins",
          fontSize: "13px",
          width: "100%",
          color: isDark ? "#f1f5f9" : "#1e293b",
          "& .MuiSelect-select": { py: 0.5 }
        }}
      >
        {options.map(opt => (
          <MenuItem key={opt.code} value={opt.code} sx={{ fontFamily: "Poppins", fontSize: "13px" }}>
            {opt.code} — {opt.desc}
          </MenuItem>
        ))}
      </Select>
    );
  }

  if (level === 2) {
    const l2s = UNIFORMAT_TAXONOMY[l1Code]?.level2 || {};
    const options = Object.keys(l2s).map(code => ({
      code,
      desc: l2s[code].desc
    }));
    return (
      <Select
        value={l2Code}
        onChange={handleLevelChange}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        size="small"
        variant="standard"
        disableUnderline
        sx={{
          fontFamily: "Poppins",
          fontSize: "13px",
          width: "100%",
          color: isDark ? "#f1f5f9" : "#1e293b",
          "& .MuiSelect-select": { py: 0.5 }
        }}
      >
        {options.map(opt => (
          <MenuItem key={opt.code} value={opt.code} sx={{ fontFamily: "Poppins", fontSize: "13px" }}>
            {opt.code} — {opt.desc}
          </MenuItem>
        ))}
      </Select>
    );
  }

  if (level === 3) {
    const l3s = UNIFORMAT_TAXONOMY[l1Code]?.level2[l2Code]?.level3 || {};
    const options = Object.keys(l3s).map(code => ({
      code,
      desc: l3s[code]
    }));
    return (
      <Select
        value={l3Code}
        onChange={handleLevelChange}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        size="small"
        variant="standard"
        disableUnderline
        sx={{
          fontFamily: "Poppins",
          fontSize: "13px",
          fontWeight: 600,
          width: "100%",
          color: l3Code === "U0000" ? (isDark ? "#f87171" : "#b91c1c") : (isDark ? "#4ade80" : "#166534"),
          "& .MuiSelect-select": { py: 0.5 }
        }}
      >
        {options.map(opt => (
          <MenuItem key={opt.code} value={opt.code} sx={{ fontFamily: "Poppins", fontSize: "13px" }}>
            {opt.code} — {opt.desc}
          </MenuItem>
        ))}
      </Select>
    );
  }

  return null;
};

const BIMUniformatTab = ({ building, selectedBuildingSection = null }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const {
    columnVisibilities,
    isSaveClicked,
    setIsSaveClicked,
    setIsSaveVisible,
    setIsLoading,
    setRefresh
  } = useContextProvider();

  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const columnMenuAnchorRef = useRef(null);
  const colVisibility_key = "bim_uniformat_grid_column_visibility";
  const defaultCols = useMemo(() => [
    "element_name",
    "ifc_entity_type",
    "storey_name",
    "volume_m3",
    "area_m2",
    "primary_material",
    "is_external",
    "uniformat_level_1",
    "uniformat_level_2",
    "uniformat_level_3",
    "uniformat_confidence",
    "requires_human_review"
  ], []);

  const visibleCols = columnVisibilities[colVisibility_key] || defaultCols;
  const [activeTab, setActiveTab] = useState("elements"); // "elements" or "materials"
  const [materialsSummary, setMaterialsSummary] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  const [summaryData, setSummaryData] = useState(null);
  const [elements, setElements] = useState([]);
  const [unsavedChanges, setUnsavedChanges] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingElements, setLoadingElements] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [syncingLci, setSyncingLci] = useState(false);
  const [syncSuccessMsg, setSyncSuccessMsg] = useState(null);
  const [uploadVisualState, setUploadVisualState] = useState("idle");
  const [uploadAnimStep, setUploadAnimStep] = useState(0);

  const buildingId = building?.id;
  const apiBase = config.apiUrl || "http://localhost:3002";
  const selectedUniformatFilter = useMemo(
    () => getUniformatFilterFromSection(selectedBuildingSection),
    [selectedBuildingSection]
  );

  useEffect(() => {
    if (!uploading) return;

    setUploadVisualState("processing");
    setUploadAnimStep(0);
    const stepTimer = setInterval(() => {
      setUploadAnimStep((prev) => (prev + 1) % 3);
    }, 1100);

    return () => clearInterval(stepTimer);
  }, [uploading]);

  const fetchSummary = useCallback(async () => {
    if (!buildingId) return;
    setLoadingSummary(true);
    try {
      const response = await fetchWithToken(
        `${apiBase}/building/${buildingId}/uniformat-summary`
      );
      if (response.ok) {
        const data = await response.json();
        setSummaryData(data);
      }
    } catch (err) {
      console.error("Failed to fetch Uniformat summary:", err);
    } finally {
      setLoadingSummary(false);
    }
  }, [buildingId, apiBase]);

  const fetchElements = useCallback(async () => {
    if (!buildingId) return;
    setLoadingElements(true);
    try {
      if (selectedUniformatFilter?.noRows) {
        setElements([]);
        return;
      }

      const params = new URLSearchParams();
      if (selectedUniformatFilter?.level1) {
        params.append("level_1", selectedUniformatFilter.level1);
      }
      if (selectedUniformatFilter?.level2) {
        params.append("level_2", selectedUniformatFilter.level2);
      }

      const query = params.toString();
      const url = `${apiBase}/building/${buildingId}/bim-elements${query ? `?${query}` : ""}`;
      const response = await fetchWithToken(url);
      if (response.ok) {
        const data = await response.json();
        setElements(data);
      }
    } catch (err) {
      console.error("Failed to fetch BIM elements:", err);
    } finally {
      setLoadingElements(false);
    }
  }, [buildingId, apiBase, selectedUniformatFilter]);

  const fetchMaterials = useCallback(async () => {
    if (!buildingId) return;
    setLoadingMaterials(true);
    try {
      const response = await fetchWithToken(
        `${apiBase}/building/${buildingId}/bim-materials-summary`
      );
      if (response.ok) {
        const data = await response.json();
        setMaterialsSummary(data);
      }
    } catch (err) {
      console.error("Failed to fetch materials summary:", err);
    } finally {
      setLoadingMaterials(false);
    }
  }, [buildingId, apiBase]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchElements();
  }, [fetchElements]);

  useEffect(() => {
    if (activeTab === "materials") {
      fetchMaterials();
    }
  }, [activeTab, fetchMaterials]);

  const handleLocalChange = useCallback((elementId, targetL3Code) => {
    let l1Code = "U";
    let l1Desc = "UNMAPPED";
    let l2Code = "U00";
    let l2Desc = "Unmapped Elements";
    let l3Code = "U0000";
    let l3Desc = "Unmapped Element";

    for (const l1 in UNIFORMAT_TAXONOMY) {
      const l2s = UNIFORMAT_TAXONOMY[l1].level2;
      for (const l2 in l2s) {
        const l3s = l2s[l2].level3;
        if (l3s[targetL3Code]) {
          l1Code = l1;
          l1Desc = UNIFORMAT_TAXONOMY[l1].desc;
          l2Code = l2;
          l2Desc = l2s[l2].desc;
          l3Code = targetL3Code;
          l3Desc = l3s[targetL3Code];
          break;
        }
      }
    }

    setElements((prev) =>
      prev.map((el) => {
        if (el.id === elementId) {
          return {
            ...el,
            uniformat_level_1_code: l1Code,
            uniformat_level_1_desc: l1Desc,
            uniformat_level_2_code: l2Code,
            uniformat_level_2_desc: l2Desc,
            uniformat_level_3_code: l3Code,
            uniformat_level_3_desc: l3Desc,
            uniformat_confidence: 1.0,
            requires_human_review: 0,
          };
        }
        return el;
      })
    );

    setUnsavedChanges((prev) => ({
      ...prev,
      [elementId]: targetL3Code,
    }));

    setIsSaveVisible(true);
  }, [setIsSaveVisible]);

  useEffect(() => {
    const saveChanges = async () => {
      const changedKeys = Object.keys(unsavedChanges);
      if (changedKeys.length === 0) {
        setIsSaveClicked(false);
        return;
      }

      setIsLoading(true);
      try {
        await Promise.all(
          changedKeys.map((id) =>
            fetchWithToken(
              `${apiBase}/building/element/${id}/override-uniformat`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ override_level_3_code: unsavedChanges[id], skip_sync: true }),
              }
            )
          )
        );
        // Trigger a single clean sync across the entire building for all updated elements
        if (buildingId) {
          await fetchWithToken(`${apiBase}/building/${buildingId}/sync-bim-lci`, { method: "POST" });
        }
        setUnsavedChanges({});
        setIsSaveVisible(false);
        fetchSummary();
        fetchElements();
        fetchMaterials();
        if (setRefresh) {
          setRefresh((prev) => !prev);
        }
      } catch (err) {
        console.error("Failed to save changes:", err);
      } finally {
        setIsLoading(false);
        setIsSaveClicked(false);
      }
    };

    if (isSaveClicked) {
      saveChanges();
    }
  }, [isSaveClicked, unsavedChanges, apiBase, fetchSummary, fetchElements, fetchMaterials, setIsLoading, setIsSaveClicked, setIsSaveVisible]);

  useEffect(() => {
    return () => {
      setIsSaveVisible(false);
      setIsSaveClicked(false);
    };
  }, [setIsSaveVisible, setIsSaveClicked]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      setUploadError(`File size (${(file.size / (1024 * 1024)).toFixed(1)} MB) exceeds maximum 100 MB limit.`);
      return;
    }

    setUploadError(null);
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetchWithToken(
        `${apiBase}/building/${buildingId}/upload-ifc`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();
      if (!response.ok) {
        const errMsg = typeof result === "object" && result !== null
          ? (result.error || result.message || JSON.stringify(result))
          : String(result);
        throw new Error(errMsg || "Failed to upload IFC model");
      }

      setUploadVisualState("done");
      setTimeout(() => setUploadVisualState("idle"), 3000);
      fetchSummary();
      fetchElements();
      fetchMaterials();
      if (setRefresh) {
        setRefresh((prev) => !prev);
      }
    } catch (err) {
      const displayErr = typeof err === "object" && err !== null
        ? (err.message || JSON.stringify(err))
        : String(err);
      setUploadError(displayErr);
      setUploadVisualState("idle");
    } finally {
      setUploading(false);
    }
  };

  const handleSyncToLci = async () => {
    if (!buildingId) return;
    try {
      setSyncingLci(true);
      setUploadError(null);
      setSyncSuccessMsg(null);
      const response = await fetchWithToken(
        `${apiBase}/building/${buildingId}/sync-bim-lci`,
        { method: "POST" }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || data?.message || "Failed to sync BIM to LCI");
      }
      setSyncSuccessMsg(`Successfully synced ${data.synced_factors || 0} environmental factors into Life Cycle Inventory building sections.`);
      setTimeout(() => setSyncSuccessMsg(null), 5000);
      if (setRefresh) {
        setRefresh((prev) => !prev);
      }
    } catch (err) {
      const displayErr = typeof err === "object" && err !== null
        ? (err.message || JSON.stringify(err))
        : String(err);
      setUploadError(displayErr);
    } finally {
      setSyncingLci(false);
    }
  };

  const rawColumnDefs = useMemo(
    () => [
      {
        ...leftAlignColDef,
        headerName: "Element Name",
        field: "element_name",
        width: 240,
        sort: "asc", // Default alphabetical sort A-Z
        cellRenderer: (params) => (
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              color: isDark ? "#f1f5f9" : "#0f172a",
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={params.value || ""}
          >
            {params.value || "Unnamed Element"}
          </Typography>
        ),
      },
      {
        ...leftAlignColDef,
        headerName: "IFC Entity Class",
        field: "ifc_entity_type",
        width: 170,
        cellRenderer: (params) => (
          <Chip
            label={params.value}
            size="small"
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "12px",
              fontWeight: 500,
              backgroundColor: isDark ? "rgba(37, 99, 235, 0.2)" : "#dbeafe",
              color: isDark ? "#60a5fa" : "#1e40af",
            }}
          />
        ),
      },
      {
        ...leftAlignColDef,
        headerName: "Storey",
        field: "storey_name",
        width: 140,
        cellRenderer: (params) => (
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              color: isDark ? "#cbd5e1" : "#334155",
            }}
          >
            {params.value || "—"}
          </Typography>
        ),
      },
      {
        ...rightAlignColDef,
        headerName: "Volume (m³)",
        field: "volume_m3",
        width: 130,
        cellRenderer: (params) => (
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              color: isDark ? "#f1f5f9" : "#0f172a",
            }}
          >
            {params.value ? Number(params.value).toFixed(3) : "0.000"}
          </Typography>
        ),
      },
      {
        ...rightAlignColDef,
        headerName: "Area (m²)",
        field: "area_m2",
        width: 130,
        cellRenderer: (params) => (
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              color: isDark ? "#f1f5f9" : "#0f172a",
            }}
          >
            {params.value ? Number(params.value).toFixed(2) : "0.00"}
          </Typography>
        ),
      },
      {
        ...leftAlignColDef,
        headerName: "Materials",
        field: "primary_material",
        width: 290,
        autoHeight: true,
        cellRenderer: (params) => {
          const raw = params.value || "Not Specified";
          if (raw.includes(" • ")) {
            const layers = raw.split(" • ");
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  py: 1,
                  width: "100%",
                }}
              >
                {layers.map((layer, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "inline-block",
                      px: 1,
                      py: 0.3,
                      borderRadius: "6px",
                      backgroundColor: isDark ? "rgba(148, 163, 184, 0.12)" : "#f8fafc",
                      border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.08)" : "#e2e8f0"}`,
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: isDark ? "#e2e8f0" : "#334155",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "270px",
                    }}
                    title={layer}
                  >
                    {layer}
                  </Box>
                ))}
              </Box>
            );
          }
          return (
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
                fontWeight: 400,
                color: raw === "Not Specified" ? (isDark ? "#64748b" : "#94a3b8") : (isDark ? "#e2e8f0" : "#1e293b"),
                fontStyle: raw === "Not Specified" ? "italic" : "normal",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={raw}
            >
              {raw}
            </Typography>
          );
        },
      },
      {
        ...centerAlignColDef,
        headerName: "Properties",
        field: "is_external",
        width: 130,
        cellRenderer: (params) => {
          const isExt = params.value;
          return (
            <Chip
              label={isExt ? "External" : "Internal"}
              size="small"
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: 500,
                backgroundColor: isExt
                  ? (isDark ? "rgba(16, 185, 129, 0.2)" : "#d1fae5")
                  : (isDark ? "rgba(107, 114, 128, 0.2)" : "#f3f4f6"),
                color: isExt
                  ? (isDark ? "#34d399" : "#065f46")
                  : (isDark ? "#9ca3af" : "#374151"),
              }}
            />
          );
        },
      },
      {
        ...leftAlignColDef,
        headerName: "Uniformat Level 1",
        field: "uniformat_level_1",
        width: 200,
        cellRenderer: (params) => (
          <UniformatSelectRenderer
            rowData={params.data}
            level={1}
            onLocalChange={handleLocalChange}
          />
        ),
      },
      {
        ...leftAlignColDef,
        headerName: "Uniformat Level 2",
        field: "uniformat_level_2",
        width: 220,
        cellRenderer: (params) => (
          <UniformatSelectRenderer
            rowData={params.data}
            level={2}
            onLocalChange={handleLocalChange}
          />
        ),
      },
      {
        ...leftAlignColDef,
        headerName: "Uniformat Level 3",
        field: "uniformat_level_3",
        width: 250,
        cellRenderer: (params) => (
          <UniformatSelectRenderer
            rowData={params.data}
            level={3}
            onLocalChange={handleLocalChange}
          />
        ),
      },
      {
        ...centerAlignColDef,
        headerName: "Confidence",
        field: "uniformat_confidence",
        width: 130,
        cellRenderer: (params) => {
          const score = params.value || 0;
          let color = "#16a34a";
          let bg = "rgba(22, 163, 74, 0.15)";
          if (score < 0.60) {
            color = "#dc2626";
            bg = "rgba(220, 38, 38, 0.15)";
          } else if (score < 0.80) {
            color = "#d97706";
            bg = "rgba(217, 119, 6, 0.15)";
          }

          return (
            <Box
              sx={{
                px: 1.5,
                py: 0.3,
                borderRadius: "6px",
                backgroundColor: bg,
                color: color,
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: "12px",
                display: "inline-block",
              }}
            >
              {(score * 100).toFixed(0)}%
            </Box>
          );
        },
      },
      {
        ...centerAlignColDef,
        headerName: "Requires Review",
        field: "requires_human_review",
        width: 150,
        cellRenderer: (params) => {
          const req = params.value;
          return req ? (
            <Chip
              label="Review Required"
              size="small"
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "11px",
                fontWeight: 600,
                backgroundColor: isDark ? "rgba(239, 68, 68, 0.2)" : "#fee2e2",
                color: isDark ? "#f87171" : "#b91c1c",
              }}
            />
          ) : (
            <Chip
              label="Verified"
              size="small"
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "11px",
                fontWeight: 500,
                backgroundColor: isDark ? "rgba(34, 197, 94, 0.15)" : "#dcfce7",
                color: isDark ? "#4ade80" : "#15803d",
              }}
            />
          );
        },
      },
    ],
    [isDark, handleLocalChange]
  );

  const materialsColumnDefs = useMemo(
    () => [
      {
        ...leftAlignColDef,
        headerName: "Material Name",
        field: "material_name",
        width: 280,
        cellRenderer: (params) => (
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              color: isDark ? "#f1f5f9" : "#0f172a",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        ...centerAlignColDef,
        headerName: "Element Count",
        field: "element_count",
        width: 150,
        cellRenderer: (params) => (
          <Chip
            label={`${params.value} elements`}
            size="small"
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "12px",
              fontWeight: 500,
              backgroundColor: isDark ? "rgba(59, 130, 246, 0.15)" : "#eff6ff",
              color: isDark ? "#60a5fa" : "#2563eb",
            }}
          />
        ),
      },
      {
        ...leftAlignColDef,
        headerName: "IFC Classes",
        field: "entity_types",
        width: 260,
        cellRenderer: (params) => (
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 400,
              color: isDark ? "#cbd5e1" : "#334155",
            }}
          >
            {params.value || "—"}
          </Typography>
        ),
      },
      {
        ...leftAlignColDef,
        headerName: "Matched Database Item",
        field: "matched_item_name",
        width: 320,
        cellRenderer: (params) => (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
                fontWeight: 600,
                color: isDark ? "#f8fafc" : "#0f172a",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.value || "—"}
            </Typography>
            {params.data?.matched_manufacturer && (
              <Typography
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "11px",
                  fontWeight: 400,
                  color: isDark ? "#94a3b8" : "#64748b",
                }}
              >
                Mfr: {params.data.matched_manufacturer}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        ...centerAlignColDef,
        headerName: "Source",
        field: "matched_source",
        width: 170,
        cellRenderer: (params) => {
          if (!params.value) return <Typography sx={{ fontSize: "12px", color: isDark ? "#64748b" : "#94a3b8" }}>—</Typography>;
          const isEnv = params.value === "env_factor_options";
          return (
            <Chip
              label={isEnv ? "env_factors" : "north_american_epd"}
              size="small"
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "11px",
                fontWeight: 600,
                backgroundColor: isEnv
                  ? (isDark ? "rgba(16, 185, 129, 0.15)" : "#ecfdf5")
                  : (isDark ? "rgba(139, 92, 246, 0.15)" : "#f5f3ff"),
                color: isEnv ? (isDark ? "#34d399" : "#059669") : (isDark ? "#a78bfa" : "#7c3aed"),
              }}
            />
          );
        },
      },
      {
        ...centerAlignColDef,
        headerName: "Confidence",
        field: "confidence_bucket",
        width: 140,
        cellRenderer: (params) => {
          const val = params.value || "UNMATCHED";
          let colorBg = isDark ? "rgba(100, 116, 139, 0.2)" : "#f1f5f9";
          let colorText = isDark ? "#94a3b8" : "#64748b";

          if (val === "HIGH") {
            colorBg = isDark ? "rgba(34, 197, 94, 0.18)" : "#dcfce7";
            colorText = isDark ? "#4ade80" : "#16a34a";
          } else if (val === "MEDIUM") {
            colorBg = isDark ? "rgba(245, 158, 11, 0.18)" : "#fef3c7";
            colorText = isDark ? "#fbbf24" : "#d97706";
          } else if (val === "LOW") {
            colorBg = isDark ? "rgba(239, 68, 68, 0.18)" : "#fee2e2";
            colorText = isDark ? "#f87171" : "#dc2626";
          }

          return (
            <Chip
              label={`${val} ${params.data?.confidence_score ? `(${(params.data.confidence_score * 100).toFixed(0)}%)` : ""}`}
              size="small"
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "11px",
                fontWeight: 600,
                backgroundColor: colorBg,
                color: colorText,
              }}
            />
          );
        },
      },
      {
        ...rightAlignColDef,
        headerName: "GWP Factor",
        field: "gwp_factor",
        width: 150,
        cellRenderer: (params) => (
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              color: isDark ? "#f1f5f9" : "#0f172a",
            }}
          >
            {params.value !== null && params.value !== undefined
              ? `${Number(params.value).toFixed(3)} ${params.data?.declared_unit ? `kgCO₂e/${params.data.declared_unit}` : "kgCO₂e"}`
              : "—"}
          </Typography>
        ),
      },
      {
        ...rightAlignColDef,
        headerName: "Total Volume (m³)",
        field: "total_volume_m3",
        width: 160,
        cellRenderer: (params) => (
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              color: isDark ? "#f1f5f9" : "#0f172a",
            }}
          >
            {params.value ? Number(params.value).toFixed(3) : "0.000"}
          </Typography>
        ),
      },
      {
        ...rightAlignColDef,
        headerName: "Total Area (m²)",
        field: "total_area_m2",
        width: 150,
        cellRenderer: (params) => (
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              color: isDark ? "#f1f5f9" : "#0f172a",
            }}
          >
            {params.value ? Number(params.value).toFixed(2) : "0.00"}
          </Typography>
        ),
      },
    ],
    [isDark]
  );

  const columnDefs = useMemo(() => {
    return rawColumnDefs.filter((col) => visibleCols.includes(col.field));
  }, [rawColumnDefs, visibleCols]);

  const getElementRowStyle = useCallback(
    (params) => {
      if (params.data?.requires_human_review) {
        return {
          backgroundColor: isDark ? "rgba(239, 68, 68, 0.08)" : "rgba(254, 226, 226, 0.5)",
          borderLeft: "3px solid #ef4444",
        };
      }
      return null;
    },
    [isDark]
  );

  const tabOptions = useMemo(
    () => [
      { label: `Elements (${elements.length})`, value: "elements" },
      { label: `Materials (${materialsSummary.length})`, value: "materials" },
    ],
    [elements.length, materialsSummary.length]
  );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: ORG_PALETTE.spacing.sectionGap,
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          p: 2.5,
          borderRadius: tok.radius.card || "12px",
          backgroundColor: isDark ? "rgba(30, 41, 59, 0.7)" : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
          boxShadow: isDark
            ? "0 4px 20px rgba(0,0,0,0.3)"
            : "0 2px 10px rgba(0,0,0,0.04)",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Poppins",
              fontWeight: 600,
              fontSize: "18px",
              color: isDark ? "#fff" : "#0f172a",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Icon icon="mdi:cube-outline" width={22} color={isDark ? "#4ade80" : "#16a34a"} />
            BIM IFC Classification & Inventory Engine
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Poppins",
              color: isDark ? "#94a3b8" : "#64748b",
              mt: 0.4,
            }}
          >
            Fast automated ingestion (Up to 100MB) with ASTM Uniformat II hierarchy and Material BOM aggregation.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            disabled={uploading || syncingLci}
            onClick={handleSyncToLci}
            startIcon={syncingLci ? <CircularProgress size={16} color="inherit" /> : <Icon icon="mdi:sync" />}
            sx={{
              fontFamily: "Poppins",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              px: 2.5,
              py: 1.2,
              borderColor: isDark ? "#3b82f6" : "#2563eb",
              color: isDark ? "#60a5fa" : "#2563eb",
              "&:hover": {
                borderColor: isDark ? "#60a5fa" : "#1d4ed8",
                backgroundColor: isDark ? "rgba(59, 130, 246, 0.1)" : "rgba(37, 99, 235, 0.05)",
              },
            }}
          >
            {syncingLci ? "Syncing to LCI..." : "Sync to LCI Sections"}
          </Button>

          <Button
            variant="contained"
            component="label"
            disabled={uploading || syncingLci}
            startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <Icon icon="mdi:cloud-upload-outline" />}
            sx={{
              fontFamily: "Poppins",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
              py: 1.2,
              minWidth: "260px",
              background: isDark
                ? "linear-gradient(135deg, #22c55e, #16a34a)"
                : "linear-gradient(135deg, #16a34a, #15803d)",
              boxShadow: uploading
                ? "0 0 0 0 rgba(34,197,94,0.45), 0 8px 24px rgba(22,163,74,0.35)"
                : "0 6px 18px rgba(22,163,74,0.24)",
              transition: "all 220ms ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 10px 24px rgba(22,163,74,0.35)",
              },
            }}
          >
            {uploading ? "Uploading IFC and Running Engine..." : "Upload IFC Model (Up to 100MB)"}
            <input type="file" accept=".ifc,.IFC" hidden onChange={handleFileUpload} />
          </Button>
        </Box>
      </Box>

      {syncSuccessMsg && (
        <Box
          sx={{
            width: "100%",
            p: 2,
            borderRadius: "8px",
            backgroundColor: isDark ? "rgba(34, 197, 94, 0.2)" : "#dcfce7",
            border: `1px solid ${isDark ? "#16a34a" : "#86efac"}`,
            color: isDark ? "#4ade80" : "#15803d",
            fontFamily: "Poppins",
            fontSize: "14px",
          }}
        >
          {syncSuccessMsg}
        </Box>
      )}

      {uploadError && (
        <Box
          sx={{
            width: "100%",
            p: 2,
            borderRadius: "8px",
            backgroundColor: isDark ? "rgba(220, 38, 38, 0.2)" : "#fee2e2",
            border: `1px solid ${isDark ? "#b91c1c" : "#fca5a5"}`,
            color: isDark ? "#f87171" : "#991b1b",
            fontFamily: "Poppins",
            fontSize: "14px",
          }}
        >
          {uploadError}
        </Box>
      )}

      <Box className="eng-flex-grid" sx={{ gap: ORG_PALETTE.spacing.metricCardGap }}>
        <Box className="eng-metric-item">
          <MetricCard
            title="Extracted Elements"
            value={summaryData ? summaryData.total_elements : 0}
            unit="Items"
            type="chain"
            variant="stage"
            bg={isDark ? "#0A1930" : "rgba(37, 99, 235, 0.08)"}
          />
        </Box>
        <Box className="eng-metric-item">
          <MetricCard
            title="Average Confidence"
            value={summaryData ? Number((summaryData.avg_confidence * 100).toFixed(1)) : 0}
            unit="%"
            type="cost"
            variant="stage"
            bg={isDark ? "#062417" : "rgba(34, 197, 94, 0.1)"}
          />
        </Box>
        <Box className="eng-metric-item">
          <MetricCard
            title="Review Queue Flagged"
            value={summaryData ? summaryData.review_required_count : 0}
            unit="Items"
            type="gwp"
            variant="stage"
            bg={isDark ? "#2A1A08" : "rgba(245, 158, 11, 0.12)"}
          />
        </Box>
        <Box className="eng-metric-item">
          <MetricCard
            title="Total Model Volume"
            value={summaryData ? Number(summaryData.total_volume_m3.toFixed(1)) : 0}
            unit="m³"
            type="shield"
            variant="stage"
            bg={isDark ? "#1E1033" : "rgba(168, 85, 247, 0.12)"}
          />
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          p: 0,
          borderRadius: tok.radius.card || "12px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
          backgroundColor: isDark ? "rgba(30, 41, 59, 0.7)" : "#ffffff",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            pt: 2.2,
            pb: 1.4,
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}`,
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: "290px", maxWidth: "360px" }}>
            <SegmentedTabs
              options={tabOptions}
              selected={activeTab}
              setSelected={setActiveTab}
              activeColor={isDark ? "#16a34a" : "#659d33"}
              dense={true}
            />
          </Box>

          <Box ref={columnMenuAnchorRef} sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            {activeTab === "elements" && (
              <>
                <Chip
                  size="small"
                  icon={<Icon icon="mdi:alert-circle-outline" />}
                  label={`${elements.filter((e) => e.requires_human_review).length} Need Review`}
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    fontSize: "11px",
                    backgroundColor: isDark ? "rgba(244,63,94,0.2)" : "#ffe4e6",
                    color: isDark ? "#fda4af" : "#be123c",
                    border: "1px solid",
                    borderColor: isDark ? "rgba(244,63,94,0.45)" : "#fda4af",
                  }}
                />
                <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
              </>
            )}
          </Box>
        </Box>

        {activeTab === "elements" ? (
          loadingElements ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ width: "100%", overflow: "hidden" }}>
              <AgGrid
                rowData={elements}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={50}
                maxHeight={elements.length > 0 ? "560px" : "220px"}
                getRowStyle={getElementRowStyle}
                footerLeftContent={
                  <Typography sx={{ fontFamily: "Poppins", fontSize: "12px", color: isDark ? "#94a3b8" : "#64748b" }}>
                    Review-highlighted rows are marked in red.
                  </Typography>
                }
                footerRightContent={
                  <Typography sx={{ fontFamily: "Poppins", fontSize: "12px", color: isDark ? "#94a3b8" : "#64748b" }}>
                    Total: {elements.length}
                  </Typography>
                }
              />
            </Box>
          )
        ) : (
          loadingMaterials ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ width: "100%", overflow: "hidden" }}>
              <AgGrid
                rowData={materialsSummary}
                columnDefs={materialsColumnDefs}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={50}
                maxHeight={materialsSummary.length > 0 ? "560px" : "220px"}
                footerRightContent={
                  <Typography sx={{ fontFamily: "Poppins", fontSize: "12px", color: isDark ? "#94a3b8" : "#64748b" }}>
                    Total Unique Materials: {materialsSummary.length}
                  </Typography>
                }
              />
            </Box>
          )
        )}
      </Box>

      <ColumnMenu
        anchorRef={columnMenuAnchorRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={rawColumnDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />

      {uploading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            borderRadius: tok.radius.card || "12px",
            backgroundColor: isDark ? "rgba(2, 6, 23, 0.58)" : "rgba(248, 250, 252, 0.7)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              minWidth: "340px",
              maxWidth: "90%",
              p: 2,
              borderRadius: "12px",
              border: `1px solid ${isDark ? "rgba(74,222,128,0.35)" : "rgba(22,163,74,0.28)"}`,
              background: isDark ? "rgba(15, 23, 42, 0.9)" : "rgba(255,255,255,0.92)",
              boxShadow: "0 12px 32px rgba(2, 6, 23, 0.22)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.3, mb: 1.3 }}>
              <img src={arivufoot} alt="logo" style={{ width: 18, height: 18, objectFit: "contain" }} />
              <CircularProgress size={18} thickness={5} sx={{ color: isDark ? "#4ade80" : "#16a34a" }} />
              <Typography sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: "14px", color: isDark ? "#e2e8f0" : "#0f172a" }}>
                Running Arivu IFC Uniformat Engine
              </Typography>
            </Box>
            <Box sx={{ display: "grid", gap: 0.75 }}>
              {["Uploading IFC (up to 100MB)", "Extracting Quantities & Layers", "Mapping Uniformat & BOM"].map((step, idx) => (
                <Box key={step} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      backgroundColor: uploadAnimStep === idx ? (isDark ? "#4ade80" : "#16a34a") : (isDark ? "#475569" : "#cbd5e1"),
                      boxShadow: uploadAnimStep === idx ? "0 0 0 4px rgba(34,197,94,0.15)" : "none",
                      transition: "all 200ms ease",
                    }}
                  />
                  <Typography sx={{ fontFamily: "Poppins", fontSize: "12px", color: isDark ? "#cbd5e1" : "#334155" }}>
                    {step}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BIMUniformatTab;
